import React, { useState, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import SEO from './components/SEO';
import { getWaitlistCount } from './firebase/waitlistService';
import './App.css';
import Section from './components/Section';
import Button from './components/Button';
import FAQ from './components/FAQ';
import Hero from './components/Hero';
import Features from './components/Features';
import ProblemSection from './components/ProblemSection/ProblemSection';
import Solutions from './components/Solutions/Solutions';
import ButlerShowcase from './components/ButlerShowcase/ButlerShowcase';
import Navbar from './components/Navbar';
import ToastContainer, { showToast } from './components/Toast';
import GradualSpacingText from './components/GradualSpacingText/GradualSpacingText';
import WaitlistCTA from './components/WaitlistCTA/WaitlistCTA';
import './RegistrationPage.css';
import Footer from './components/Footer';
import { MessageCircle, Send, Copy } from 'lucide-react';

function App() {
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved) return saved;
      return (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) ? 'dark' : 'light';
    } catch (e) {
      return 'light';
    }
  });
  const [showContact, setShowContact] = useState(false);



  useEffect(() => {
    const detectMobile = () => {
      try {
        const hasTouch = (typeof window !== 'undefined') && (
          'ontouchstart' in window || navigator.maxTouchPoints > 0 || (window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
        );
        setIsMobile(Boolean(hasTouch));
      } catch (e) {
        setIsMobile(false);
      }
    };
    detectMobile();
    window.addEventListener('resize', detectMobile);
    return () => window.removeEventListener('resize', detectMobile);
  }, []);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const count = await getWaitlistCount();
        setWaitlistCount(count);
      } catch (error) {
        console.error('Error fetching waitlist count:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCount();
  }, []);

  useEffect(() => {
    try {
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem('theme', theme);
    } catch (e) {
      // ignore
    }
  }, [theme]);

  // Intersection Observer to reveal elements with '.reveal' class
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleFormSuccess = () => {
    // Refresh count after successful signup
    getWaitlistCount().then(count => setWaitlistCount(count));
  };

  // scrollToForm removed - navigation now goes to the standalone registration page

  const scrollToFeatures = () => {
    const featuresElement = document.getElementById('features-section');
    if (featuresElement) {
      featuresElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goToRegistration = () => {
    // Show the contact / request-a-quote section in-page and scroll with header offset
    try {
      setShowContact(true);
      // Wait for DOM update then scroll with offset so header doesn't cover content
      setTimeout(() => {
        const el = document.getElementById('contact-section');
        if (el) {
          // Ensure reveal animations run for dynamically inserted contact section
          el.classList.add('is-visible');
          const header = document.querySelector('.hero-header');
          const headerHeight = (header && header.offsetHeight) ? header.offsetHeight : 80;
          const offset = 20; // extra spacing
          const top = el.getBoundingClientRect().top + window.scrollY - headerHeight - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 120);
    } catch (e) {
      // fallback to navigation if something goes wrong
      window.location.href = '/registration';
    }
  };

  const closeContact = () => {
    setShowContact(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const shareOnWhatsApp = () => {
    const text = encodeURIComponent('Contact Expert Supply Chain Logistics LLC for reliable dry-van capacity and freight solutions.');
    const url = window.location.href;
    window.open(`https://wa.me/?text=${text}%20${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnTelegram = () => {
    const text = encodeURIComponent('Contact Expert Supply Chain Logistics LLC for reliable dry-van capacity and freight solutions.');
    const url = window.location.href;
    window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${text}`, '_blank');
  }; 

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      // Non-blocking toast instead of alert
      try { showToast('Link copied to clipboard!'); } catch (e) { /* fallback */ }
    });
  };

  return (
    <HelmetProvider>
      <div className="App">

      {/* Blob cursor disabled on homepage to match static hero visuals */}
      {/* Navbar */}
      <Navbar
        onHomeClick={scrollToTop}
        onFeaturesClick={scrollToFeatures}
        onContactClick={goToRegistration}
        onThemeToggle={() => setTheme(prev => prev === 'dark' ? 'light' : 'dark')}
        onJoinClick={goToRegistration}
        theme={theme}
      />

      {/* Toast container (global) */}
      <ToastContainer />

      {/* Site-level SEO */}
      <SEO
        title={"Nationwide Transportation and Freight Solutions"}
        description={"Expert Supply Chain Logistics LLC — Reliable dry-van capacity and freight solutions across the U.S. Contact 470-406-8861."}
        canonical={"https://expertsupplychainlogistics.com/"}
        image={`${process.env.PUBLIC_URL}/assets/images/demo.png`}
        url={"https://expertsupplychainlogistics.com/"}
      />

      {/* Hero Section */}
      <Hero
        onInquireClick={goToRegistration}
        onFeaturesClick={scrollToFeatures}
        waitlistCount={waitlistCount}
        loading={loading}
        theme={theme}
        onThemeToggle={() => setTheme(prev => prev === 'dark' ? 'light' : 'dark')}
        // Use PUBLIC_URL so the path resolves correctly in dev and production
        heroImageUrl={`${process.env.PUBLIC_URL}/assets/images/demo.png`}
      />

      <ProblemSection />

      {/* Solution Features Section */}
      <Solutions />

      <ButlerShowcase onFeaturesClick={scrollToFeatures} />

      {/* Features Section */}
      <Features />
      {/* <Mission /> */}

      <WaitlistCTA onJoinClick={goToRegistration} />

      {/* Action Section */}
      <Section className="cta-section reveal">
        <h2 className="section-headline"><GradualSpacingText text={"Ready to optimize your freight?"} /></h2>
        <p className="cta-text">Contact us today to discuss your transportation needs or request a customized quote for your lanes.</p>
        <div className="share-buttons">
          <Button onClick={shareOnWhatsApp} variant="secondary">
            <MessageCircle className="btn-icon" />
            WhatsApp Inquiry
          </Button>
          <Button onClick={copyLink} variant="secondary">
            <Copy className="btn-icon" />
            Copy Website Link
          </Button>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="faq-section reveal">
        <FAQ />
      </Section>

      {/* Final CTA */}
      <Section className="final-cta-section reveal">
        <p className="final-text">Request a quote or capacity inquiry - Expert Supply Chain Logistics is ready to support your freight needs nationwide.</p>
        <Button onClick={goToRegistration} variant="primary" className="final-button">
          Request Quote
        </Button>
      </Section>

      {/* In-page Contact / Request Quote section (shown when user requests registration/contact) */}
      {showContact && (
        <div id="contact-section" className="registration-page reveal">
          <div className="registration-container registration-layout">
            <div className="left-graphic" aria-hidden>
              <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Expert Supply Chain Logistics" />
            </div>
            <div className="right-content">
              <h2 className="registration-title"><GradualSpacingText text={"Contact Us"} spacing="0.03em" stagger={18} /></h2>
              <div className="contact-info">
                <p className="contact-phone"><GradualSpacingText text={"Phone:"} spacing="0.02em" stagger={10} /><span className="contact-link"> <a href="tel:4704068861">470-406-8861</a></span></p>
                <p className="contact-email"><GradualSpacingText text={"Email:"} spacing="0.02em" stagger={10} /><span className="contact-link"> <a href="mailto:muradkoket@gmail.com">muradkoket@gmail.com</a></span></p>
                <p className="hint">Call or email us and we'll respond promptly.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
    </HelmetProvider>
  );
}

export default App;

